import { ResultOf } from 'gql.tada';
import debounce from 'lodash/debounce';
import { CheckIcon, LoaderCircleIcon } from 'lucide-react';
import { useEffect, useRef } from 'react';

import {
  Box,
  ComboboxOption,
  useComboboxInputValue,
  useSetComboboxInputValue,
} from '@eluve/components';

import { UserOption } from './UserOption';
import { tenantUserOptionFragment } from './operations';

export interface UserComboboxContentProps {
  selectedUser: ResultOf<typeof tenantUserOptionFragment> | null;
  searchResults: ResultOf<typeof tenantUserOptionFragment>[];
  loading: boolean;
  handleUserSelected: (userId: string) => void;
  tenantId: string;
  debouncedSearch: ReturnType<typeof debounce>;
  setSearchResults: React.Dispatch<
    React.SetStateAction<ResultOf<typeof tenantUserOptionFragment>[]>
  >;
}

export const UserComboboxContent: React.FC<UserComboboxContentProps> = ({
  selectedUser,
  searchResults,
  loading,
  handleUserSelected,
  tenantId: _tenantId,
  debouncedSearch,
  setSearchResults,
}) => {
  const inputValue = useComboboxInputValue();
  const setInputValue = useSetComboboxInputValue();

  const prevInputRef = useRef(inputValue);

  useEffect(
    function searchOnInputChange() {
      if (prevInputRef.current !== inputValue) {
        prevInputRef.current = inputValue;

        if (!inputValue || inputValue.length < 2) {
          setSearchResults([]);
        } else {
          debouncedSearch(inputValue);
        }
      }
    },
    [inputValue, debouncedSearch, setSearchResults],
  );

  useEffect(
    function clearResultsOnMount() {
      setSearchResults([]);
    },
    [setSearchResults],
  );

  return (
    <>
      {loading && (
        <Box vStack className="items-center justify-center py-4">
          <LoaderCircleIcon className="size-6 animate-spin text-bg-accent-strong-primary" />
          <span className="mt-2 text-text-secondary">Searching users...</span>
        </Box>
      )}

      {!loading && inputValue.length > 0 && inputValue.length < 2 && (
        <div className="px-4 py-2 text-text-tertiary">
          Type at least 2 characters to search
        </div>
      )}

      {!loading && inputValue.length >= 2 && searchResults.length === 0 && (
        <div className="px-4 py-2 text-text-tertiary">No users found</div>
      )}

      {!loading &&
        searchResults.length > 0 &&
        searchResults.map((user) => {
          return (
            <ComboboxOption
              key={user.id}
              onSelect={() => {
                handleUserSelected(user.id);
                setInputValue('');
                setSearchResults([]);
              }}
            >
              <Box hStack fullWidth>
                <Box className="flex-1">
                  <UserOption {...user} />
                </Box>
                {selectedUser?.id === user.id && (
                  <CheckIcon className="w-max text-bg-accent-strong-primary" />
                )}
              </Box>
            </ComboboxOption>
          );
        })}
    </>
  );
};
